// =============================================================================
// components/KpiIncidents/IncidentChart.jsx
//
// FIX — removed the P5-P95 / Avg±2σ band entirely. Even though
// computeYAxisRange never looked at band values directly, allowDataOverflow
// is false, and Recharts EXPANDS the axis to fit any rendered series that
// falls outside the given domain — including the band, which can carry
// extreme statistical outliers (see the old comment about Avg+2σ hitting
// 1,235,938 on a high-stdev cell). That silently overrode the intended
// Y-axis scale. Removing the band's Area entirely is simpler and more
// robust than trying to sanitise it — the actual KPI curve, SLA line and
// median were always the primary things operators read here anyway.
// =============================================================================
import { useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
	ComposedChart, Line, XAxis, YAxis, CartesianGrid,
	Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { TECH_COLOR } from '../../constants/kpiConstants';

function fmtDate(d) {
	if (!d) return '';
	try {
		return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
	} catch (e) { return ''; }
}

// -----------------------------------------------------------------------------
// computeYAxisRange — dynamic scale based only on kpiValue + SLA.
// No outlier guard on kpiValue — real spikes must be visible.
// -----------------------------------------------------------------------------
function computeYAxisRange(chartData, slaThreshold, direction) {
	const values = [];

	chartData.forEach(function (h) {
		if (h.kpiValue != null && isFinite(h.kpiValue)) {
			values.push(h.kpiValue);
		}
	});
	if (slaThreshold != null && isFinite(slaThreshold)) {
		values.push(slaThreshold);
	}

	if (values.length === 0) return [0, 100];

	const dataMin = values.reduce(function (a, b) { return a < b ? a : b; }, Infinity);
	const dataMax = values.reduce(function (a, b) { return a > b ? a : b; }, -Infinity);
	const range   = dataMax - dataMin;
	const padding = Math.max(range * 0.15, dataMax * 0.02, 1);

	let yMin = dataMin - padding;
	let yMax = dataMax + padding;

	if (yMin < 0) yMin = 0;

	if (direction === 0 && dataMax <= 100 && yMax > 100) {
		yMax = 100;
	}

	return [Math.floor(yMin), Math.ceil(yMax)];
}

// -----------------------------------------------------------------------------
// CustomTooltip
// -----------------------------------------------------------------------------
function CustomTooltip({ active, payload, label }) {
	if (!active || !payload || !payload.length) return null;
	return (
		<Box sx={{
			bgcolor: 'rgba(255,255,255,0.98)',
			border: '1px solid #e5e7eb',
			borderRadius: 1, p: 1, boxShadow: 2,
			fontSize: '0.75rem', minWidth: 140,
		}}>
			<Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
				{label}
			</Typography>
			{payload.map(function (p) {
				if (p.value == null) return null;

				return (
					<Box key={p.dataKey} sx={{
						display: 'flex', justifyContent: 'space-between', gap: 1.5,
						color: p.color,
					}}>
						<span>{p.name}</span>
						<Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
							{Number(p.value).toFixed(3)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}

export default function IncidentChart({ incident, history, loading, height }) {
	const chartHeight = height || 240;
	const techColor   = TECH_COLOR[incident.tech] || '#6366f1';
	const method      = incident.detectionMethod || 'PERCENTILE';

	// Prepare data. No more band — just the date label and whatever fields
	// history already carries (kpiValue, p50Baseline/avgBaseline, etc.).
	const chartData = useMemo(function () {
		if (!history || history.length === 0) return [];

		return history.map(function (h) {
			const obj = Object.assign({}, h);
			obj.dateLabel = fmtDate(h.date);
			return obj;
		});
	}, [history]);

	// Dynamic Y scale
	const yDomain = useMemo(function () {
		return computeYAxisRange(chartData, incident.slaThreshold, incident.direction);
	}, [chartData, incident.slaThreshold, incident.direction]);

	// Quarantine end label — the dateLabel of the last point strictly before
	// startDate. Used as a vertical marker ("Quarantine end").
	// We find it by comparing raw timestamps so gaps in data don't matter.
	const quarantineEndLabel = useMemo(function () {
		if (!chartData.length || !incident.startDate) return null;

		const startTs = new Date(incident.startDate).getTime();
		let label = null;
		chartData.forEach(function (h) {
			if (new Date(h.date).getTime() < startTs) label = h.dateLabel;
		});
		return label;
	}, [chartData, incident.startDate]);

	// --- Early returns -------------------------------------------------------
	if (loading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight, gap: 1, flexDirection: 'column' }}>
				<CircularProgress size={28} />
				<Typography variant="caption" color="text.secondary">Loading history</Typography>
			</Box>
		);
	}

	if (!history || history.length === 0) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
				<Typography variant="body2" color="text.secondary">
					No history available.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<ResponsiveContainer width="100%" height={chartHeight}>
				<ComposedChart data={chartData} margin={{ top: 20, right: 12, bottom: 0, left: 0 }}>
					<defs>
						<linearGradient id={'lineGrad-' + incident.incidentId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%"   stopColor={techColor} stopOpacity={1} />
							<stop offset="100%" stopColor={techColor} stopOpacity={0.85} />
						</linearGradient>
					</defs>

					<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
					<XAxis
						dataKey="dateLabel"
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
					/>
					<YAxis
						domain={yDomain}
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
						width={40}
						allowDataOverflow={false}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Legend
						iconSize={10}
						wrapperStyle={{ fontSize: '0.7rem', paddingTop: 4 }}
						iconType="line"
					/>

					{/* Quarantine end — vertical purple dashed line on the last
					    pre-incident data point. Marks where quarantine monitoring
					    ended and the live incident began. */}
					{quarantineEndLabel && (
						<ReferenceLine
							x={quarantineEndLabel}
							stroke="#7c3aed"
							strokeDasharray="4 2"
							strokeWidth={1.5}
							label={{
								value: 'Q. end',
								position: 'insideTopRight',
								fontSize: 9,
								fill: '#7c3aed',
								fontWeight: 600,
							}}
						/>
					)}

					{/* SLA threshold — horizontal red line */}
					<ReferenceLine
						y={incident.slaThreshold}
						stroke="#dc2626"
						strokeDasharray="5 3"
						strokeWidth={1.25}
						label={{
							value: 'SLA ' + Number(incident.slaThreshold).toFixed(2),
							position: 'insideTopRight',
							fontSize: 9,
							fill: '#dc2626',
						}}
					/>

					{/* Incident opened — vertical amber line */}
					{incident.startDate && (
						<ReferenceLine
							x={fmtDate(incident.startDate)}
							stroke="#f59e0b"
							strokeDasharray="4 2"
							strokeWidth={1.5}
							label={{
								value: 'Opened',
								position: 'insideTopLeft',
								offset: 10,
								fontSize: 9,
								fill: '#f59e0b',
								fontWeight: 600,
							}}
						/>
					)}

					{/* Median / Avg */}
					{method === 'PERCENTILE' ? (
						<Line type="monotone" dataKey="p50Baseline"
							name="Median (P50)"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					) : (
						<Line type="monotone" dataKey="avgBaseline"
							name="Average"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					)}

					{/* Actual value — bold coloured line */}
					<Line
						type="monotone"
						dataKey="kpiValue"
						name="Actual value"
						stroke={'url(#lineGrad-' + incident.incidentId + ')'}
						strokeWidth={2.5}
						dot={{ r: 3, fill: techColor, strokeWidth: 0 }}
						activeDot={{ r: 5, fill: techColor, stroke: 'white', strokeWidth: 2 }}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</Box>
	);
}